/**
 * Page "Mes cours" — portail étudiant.
 * Liste les supports de cours (PDF/Word) partagés par les formateurs,
 * regroupés par matière puis par semestre.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  FileText, FileType2, Download, BookOpen, Loader2, Search, ChevronDown,
} from 'lucide-react';
import { PortailLayout } from '../PortailLayout';
import { portailApi } from '../services/portailApi';

interface Support {
  id: number;
  titre: string;
  description: string;
  nom_original: string;
  type_fichier: 'pdf' | 'word' | 'autre';
  taille_octets: number;
  taille_humaine: string;
  ordre: number;
  formateur_nom: string;
  nb_telechargements: number;
  date_creation: string;
}

interface Groupe {
  matiere_id: number | null;
  matiere_libelle: string;
  semestre: number | null;
  supports: Support[];
}

export default function MesCours() {
  const [groupes, setGroupes] = useState<Groupe[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filtreSemestre, setFiltreSemestre] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [groupesOuverts, setGroupesOuverts] = useState<Set<string>>(new Set());

  useEffect(() => {
    (async () => {
      try {
        const r = await portailApi.getSupportsCours();
        setGroupes(r.groupes || []);
      } catch (e: any) {
        setError(e?.message || 'Impossible de charger les supports.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Auto-ouvrir tous les groupes au chargement (et à chaque changement réel du contenu).
  // Avant : dep `[groupes.length]` ratait les cas où le contenu change sans
  // que la longueur change (re-fetch sur un autre semestre, par ex.).
  useEffect(() => {
    if (groupes.length > 0) {
      setGroupesOuverts(new Set(groupes.map(g => `${g.matiere_id}-${g.matiere_libelle}`)));
    }
  }, [groupes]);

  const semestresDisponibles = useMemo(() => {
    const set = new Set<number>();
    groupes.forEach(g => { if (g.semestre) set.add(g.semestre); });
    return Array.from(set).sort();
  }, [groupes]);

  const groupesFiltres = useMemo(() => {
    let arr = groupes;
    if (filtreSemestre !== null) {
      arr = arr.filter(g => g.semestre === filtreSemestre);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr
        .map(g => ({
          ...g,
          supports: g.supports.filter(s =>
            s.titre.toLowerCase().includes(q)
            || s.description.toLowerCase().includes(q)
            || s.formateur_nom.toLowerCase().includes(q)
          ),
        }))
        .filter(g => g.supports.length > 0);
    }
    return arr;
  }, [groupes, filtreSemestre, search]);

  const toggleGroupe = (cle: string) => {
    setGroupesOuverts(prev => {
      const next = new Set(prev);
      if (next.has(cle)) next.delete(cle); else next.add(cle);
      return next;
    });
  };

  const totalSupports = groupesFiltres.reduce((acc, g) => acc + g.supports.length, 0);

  return (
    <PortailLayout showHeader title="Mes cours">
      <div className="space-y-3">
        {/* Bandeau info */}
        <div className="bg-blue-900/20 border border-blue-800/50 rounded-xl p-3 text-sm text-blue-200 flex items-start gap-2">
          <BookOpen size={16} className="flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold mb-0.5">Supports de vos formateurs</div>
            <p className="text-xs text-blue-300/80">
              Téléchargez les PDF et documents partagés par vos formateurs.
            </p>
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center gap-2 py-10 text-gray-400 text-sm">
            <Loader2 size={16} className="animate-spin" /> Chargement…
          </div>
        )}

        {error && (
          <div className="bg-red-900/30 border border-red-800 text-red-200 rounded-lg p-3 text-sm">
            {error}
          </div>
        )}

        {!loading && !error && groupes.length === 0 && (
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center">
            <FileText size={36} strokeWidth={1.3} className="mx-auto mb-3 text-gray-500" />
            <div className="text-sm font-medium text-gray-300">Aucun support disponible</div>
            <p className="text-xs text-gray-500 mt-1 max-w-xs mx-auto">
              Vos formateurs n'ont pas encore déposé de documents pour votre classe.
              Revenez plus tard.
            </p>
          </div>
        )}

        {!loading && groupes.length > 0 && (
          <>
            {/* Filtres */}
            <div className="flex gap-2 items-center flex-wrap">
              <div className="relative flex-1 min-w-[160px]">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher un cours…"
                  className="w-full pl-8 pr-2 py-2 text-xs bg-gray-900 border border-gray-800 rounded-lg text-gray-100 focus:outline-none focus:border-blue-500" />
              </div>
              {semestresDisponibles.length > 1 && (
                <div className="flex gap-1 bg-gray-900 border border-gray-800 rounded-lg p-0.5">
                  <button onClick={() => setFiltreSemestre(null)}
                    className={`px-2.5 py-1 text-xs font-semibold rounded ${
                      filtreSemestre === null
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-300 hover:bg-gray-800'
                    }`}>
                    Tous
                  </button>
                  {semestresDisponibles.map(s => (
                    <button key={s} onClick={() => setFiltreSemestre(s)}
                      className={`px-2.5 py-1 text-xs font-semibold rounded ${
                        filtreSemestre === s
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-300 hover:bg-gray-800'
                      }`}>
                      S{s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="text-[11px] text-gray-500">
              {totalSupports} support{totalSupports > 1 ? 's' : ''} disponible{totalSupports > 1 ? 's' : ''}
            </div>

            {/* Groupes */}
            <div className="space-y-2">
              {groupesFiltres.length === 0 && (
                <div className="text-center py-6 text-sm text-gray-500">
                  Aucun support ne correspond à votre recherche.
                </div>
              )}
              {groupesFiltres.map(g => {
                const cle = `${g.matiere_id}-${g.matiere_libelle}`;
                const ouvert = groupesOuverts.has(cle);
                return (
                  <section key={cle} className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                    <button onClick={() => toggleGroupe(cle)}
                      className="w-full px-3 py-2.5 flex items-center gap-3 bg-blue-900/30 hover:bg-blue-900/40 transition text-left">
                      <BookOpen size={14} className="text-blue-300 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-blue-100 truncate">{g.matiere_libelle}</div>
                        <div className="text-[10px] text-blue-300/70 mt-0.5">
                          {g.semestre ? `Semestre ${g.semestre}` : ''} • {g.supports.length} document{g.supports.length > 1 ? 's' : ''}
                        </div>
                      </div>
                      <ChevronDown size={14} className={`text-blue-300 transition-transform ${ouvert ? 'rotate-180' : ''}`} />
                    </button>
                    {ouvert && (
                      <ul className="divide-y divide-gray-800">
                        {g.supports.map(s => (
                          <li key={s.id} className="px-3 py-2.5">
                            <div className="flex items-start gap-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                s.type_fichier === 'pdf'
                                  ? 'bg-red-900/40 text-red-300'
                                  : 'bg-blue-900/40 text-blue-300'
                              }`}>
                                {s.type_fichier === 'pdf' ? <FileType2 size={18} /> : <FileText size={18} />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-100 leading-snug">
                                  {s.titre}
                                </div>
                                {s.description && (
                                  <div className="text-[11px] text-gray-400 mt-0.5 line-clamp-2">
                                    {s.description}
                                  </div>
                                )}
                                <div className="text-[10px] text-gray-500 mt-1 flex items-center gap-2 flex-wrap">
                                  <span>{s.formateur_nom}</span>
                                  <span>• {s.taille_humaine}</span>
                                  <span>• {new Date(s.date_creation).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>
                                </div>
                              </div>
                              <a href={portailApi.urlTelechargerSupport(s.id)}
                                download={s.nom_original}
                                target="_blank" rel="noopener noreferrer"
                                className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded inline-flex items-center justify-center flex-shrink-0"
                                title="Télécharger">
                                <Download size={14} />
                              </a>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                );
              })}
            </div>
          </>
        )}
      </div>
    </PortailLayout>
  );
}
